import React from 'react';
import './LandingPageV2.css';
import './CareersPage.css';
import { SubpageHeader, SubpageFooter } from './SubpageChrome';

/* /careers — open roles. Modeled on sawbridgeai.com/careers; applications go through /contact. */

const applyHref = (role: string) => `/contact?role=${encodeURIComponent(role)}`;

type Role = { title: string; type: string; location: string; summary: string; points: string[] };

const OPEN_ROLES: Role[] = [
  {
    title: 'AI Researcher',
    type: 'Full-time',
    location: 'NYC / SF Bay Area / Remote',
    summary:
      'Push the frontier of trustworthy agentic AI. You will design agents that reason over proformas, leases, zoning codes, and market data, with deterministic engines and traceable answers, not hallucinated numbers.',
    points: [
      'Agentic reasoning, tool use, and retrieval over real estate documents and data',
      'Evaluation harnesses that hold agents to auditable, verifiable output',
      'Experience with LLM fine-tuning, grounding, or program synthesis a plus',
    ],
  },
  {
    title: 'Senior Software Developer',
    type: 'Full-time',
    location: 'NYC / SF Bay Area / Remote',
    summary:
      'Build the product end to end, from data pipelines and deterministic financial engines to the interfaces developers, operators, and brokers use every day.',
    points: [
      'Full-stack: TypeScript/React, Python backends, and cloud infrastructure',
      'Ship fast without breaking trust: guardrails, approvals, and audit trails built in',
      'Experience with financial modeling, GIS, or document processing a plus',
    ],
  },
  {
    title: 'GTM Specialist',
    type: 'Full-time',
    location: 'NYC / SF Bay Area / Remote',
    summary:
      'Own how Metlas reaches the market. You will work directly with developers, owner-operators, property managers, and brokerages, turning early conversations into lasting partnerships.',
    points: [
      'Go-to-market strategy, positioning, and pipeline for our product lines',
      'Hands-on with founders: demos, pilots, pricing, and partnerships',
      'Real estate industry experience strongly preferred',
    ],
  },
];

const CareersPage: React.FC = () => (
  <div className="mlp-page">
    <SubpageHeader />

    <main>
      <section className="mlp-careers-hero">
        <div className="mlp-wrap">
          <p className="mlp-kicker mlp-kicker--kb">We’re hiring</p>
          <h1 className="mlp-careers-title">
            Build the intelligence layer <em>for real estate.</em>
          </h1>
          <p className="mlp-careers-lead">
            We’re a small team encoding how veteran developers, operators, and brokers work into AI they can
            actually trust. Come do the best work of your career.
          </p>
        </div>
      </section>

      <section className="mlp-section mlp-section--sand">
        <div className="mlp-wrap">
          <div className="mlp-section-head">
            <p className="mlp-kicker">Open roles</p>
            <h2 className="mlp-h2">Who we’re looking for</h2>
          </div>
          <div className="mlp-roles">
            {OPEN_ROLES.map((role) => (
              <article key={role.title} className="mlp-role">
                <header className="mlp-role-head">
                  <h3>{role.title}</h3>
                  <p className="mlp-role-meta">
                    {role.type} · {role.location}
                  </p>
                </header>
                <p className="mlp-role-summary">{role.summary}</p>
                <ul>
                  {role.points.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
                <a className="mlp-role-apply" href={applyHref(role.title)}>
                  Apply now <span aria-hidden="true">→</span>
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mlp-section">
        <div className="mlp-wrap mlp-careers-cta">
          <h2 className="mlp-h2">Don’t see your role?</h2>
          <p>
            If you’re exceptional at what you do and excited about real estate AI, we want to hear from you
            anyway.
          </p>
          <a className="mlp-btn mlp-btn--ghost" href={applyHref('Open application')}>
            Get in touch
          </a>
        </div>
      </section>

      <SubpageFooter />
    </main>
  </div>
);

export default CareersPage;
